<template>
  <div>
    <!-- A schedule record IS the timer — there's no "When I ask" for it, so no mode toggle. -->
    <v-btn-toggle
      v-if="type !== 'schedule'"
      :model-value="when.mode"
      mandatory
      divided
      rounded="pill"
      density="comfortable"
      color="primary"
      class="mb-2"
      @update:model-value="setMode"
    >
      <v-btn v-for="m in modes" :key="m.k" :value="m.k">{{ m.label }}</v-btn>
    </v-btn-toggle>

    <!-- Same warning the Advanced FactoryEditor shows under its Continuous checkbox. -->
    <v-alert v-if="when.mode === 'continuous'" type="warning" density="compact" variant="tonal" class="mt-3 py-1">
      This factory is continuous. It will execute until paused or cancelled. If the total-run cap is enabled, it stops after the configured number of completed full passes when the last step finishes. Per-step limits still apply separately.
    </v-alert>

    <div v-if="when.mode === 'schedule'" class="d-flex align-center flex-wrap ga-2 mt-4 text-body-1 text-medium-emphasis">
      <span>Run</span>
      <ChipSelect :model-value="when.freq || 'Daily'" :options="['Daily', 'Weekly', 'Monthly']" @update:model-value="(v) => patch({ freq: v })" />
      <template v-if="when.freq === 'Weekly'">
        <span>on</span>
        <ChipSelect :model-value="when.day || 'Monday'" :options="days" @update:model-value="(v) => patch({ day: v })" />
      </template>
      <template v-if="when.freq === 'Monthly'">
        <span>on day</span>
        <v-number-input
          :model-value="when.dayOfMonth || 1"
          :min="1"
          :max="31"
          control-variant="stacked"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 120px"
          @update:model-value="(v) => patch({ dayOfMonth: v })"
        />
      </template>
      <span>at</span>
      <ChipSelect :model-value="when.time || '9:00 AM'" :options="times" icon="mdi-clock-outline" @update:model-value="(v) => patch({ time: v })" />

      <!-- Email delivery exists on ScheduledRun only — factory schedule records have no sendEmail. -->
      <div v-if="type === 'schedule'" class="d-flex align-center ga-2 mt-1" style="flex-basis: 100%">
        <v-icon icon="mdi-email-outline" size="small" />
        <v-checkbox
          :model-value="when.sendEmail !== false"
          density="compact"
          hide-details
          :label="`Email the result to me${email ? ` (${email})` : ''}`"
          @update:model-value="(v) => patch({ sendEmail: !!v })"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import ChipSelect from './ChipSelect.vue'
import type { AutoWhen, AutomationType, WhenMode } from '@/types/automations'

@Options({
  components: { ChipSelect },
  props: {
    type: { type: String, required: true },
    when: { type: Object, required: true }
  },
  emits: ['update:when'],
  computed: {
    // The account email the result is delivered to — Advanced shows the same address (ScheduledRunCard).
    ...mapGetters(['email'])
  }
})
export default class WhenEditor extends Vue {
  type!: AutomationType
  when!: AutoWhen
  email!: string

  days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  times = ['7:00 AM', '8:00 AM', '9:00 AM', '12:00 PM', '5:00 PM', '9:00 PM']

  get modes(): { k: WhenMode; label: string }[] {
    // Workflows can't be scheduled directly (they'd be wrapped in a factory), so they only run on demand.
    if (this.type === 'workflow') {
      return [{ k: 'ondemand', label: 'When I ask' }]
    }
    // Factory schedules are separate records; the lens manages the factory's first one
    // (see factorySchedulePayloadFromAutomation) — picking another mode disables it.
    if (this.type === 'factory') {
      return [
        { k: 'ondemand', label: 'When I ask' },
        { k: 'schedule', label: 'On a schedule' },
        { k: 'continuous', label: 'Continuously' }
      ]
    }
    return [
      { k: 'ondemand', label: 'When I ask' },
      { k: 'schedule', label: 'On a schedule' }
    ]
  }

  setMode(k: WhenMode) {
    if (k === 'schedule') {
      this.$emit('update:when', {
        mode: 'schedule',
        freq: this.when.freq || 'Daily',
        day: this.when.day || 'Monday',
        dayOfMonth: this.when.dayOfMonth || 1,
        time: this.when.time || '9:00 AM',
        sendEmail: this.when.sendEmail === undefined ? true : this.when.sendEmail
      })
    } else {
      this.$emit('update:when', { ...this.when, mode: k })
    }
  }

  patch(p: Partial<AutoWhen>) {
    this.$emit('update:when', { ...this.when, ...p })
  }
}
</script>
